import type { InspectorResource, StateFinding } from '@/lib/apiTypes'
import { Tooltip } from '@/shared/components/Tooltip'
import { ExternalLinkIcon } from '@/shared/components/Icons'
import { AttributeValue } from '@/features/state/components/AttributeValue'
import { SeverityBadge } from '@/shared/components/Badge'
import { CHANGE_STYLE, resourceFindings } from '@/features/state/lib/inspector'

export function ResourceDetail({
  res, findings, onViewFindings, compareMode, replacementCause,
}: {
  res: InspectorResource
  findings: StateFinding[]
  onViewFindings: () => void
  compareMode: boolean
  /** Plan replacement cause for this resource, when the inspector is reused from a plan detail. */
  replacementCause?: string
}) {
  const resFindings = resourceFindings(res, findings)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.85rem', color: 'var(--text)' }}>{res.address}</span>
          {res.mode === 'data' && <span className="badge badge-neutral" style={{ fontSize: '0.55rem' }}>data</span>}
          {compareMode && res.change && res.change !== 'unchanged' && (
            <span className="badge" style={{ fontSize: '0.55rem', color: CHANGE_STYLE[res.change].color, borderColor: CHANGE_STYLE[res.change].color }}>
              {CHANGE_STYLE[res.change].label}
            </span>
          )}
          {replacementCause && (
            <span className="badge badge-amber" style={{ fontSize: '0.55rem' }} title={`Plan forces replacement: ${replacementCause}`}>
              ⟳ forces replacement
            </span>
          )}
        </div>
        {replacementCause && (
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--amber)', marginTop: 4 }}>
            forces replacement: {replacementCause}
          </div>
        )}
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)', marginTop: 4 }}>
          {res.provider.replace(/^provider\["|"\]$/g, '')} · {res.instances.length} instance{res.instances.length !== 1 ? 's' : ''}
        </div>
      </div>

      {resFindings.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--bg3)', borderRadius: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text2)' }}>
            {resFindings.length} finding{resFindings.length !== 1 ? 's' : ''} on this resource
          </span>
          {resFindings.slice(0, 3).map((f) => <SeverityBadge key={f.id} severity={f.severity} />)}
          <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.62rem', display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={onViewFindings}>
            View in Findings <ExternalLinkIcon size={11} />
          </button>
        </div>
      )}

      {res.instances.map((inst, idx) => (
        <div key={idx}>
          {res.instances.length > 1 && (
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)', marginBottom: 6, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              instance {inst.index_key === null ? `#${idx}` : `[${JSON.stringify(inst.index_key)}]`}
              {compareMode && inst.change && inst.change !== 'unchanged' && (
                <span style={{ marginLeft: 8, color: CHANGE_STYLE[inst.change].color }}>{CHANGE_STYLE[inst.change].label}</span>
              )}
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th style={{ width: '32%' }}><Tooltip text="Attribute key recorded in state for this resource instance." placement="top"><span tabIndex={0} className="cursor-help">Attribute</span></Tooltip></th><th><Tooltip text="The value held in state. Secrets are redacted before they reach the browser." placement="top"><span tabIndex={0} className="cursor-help">Value</span></Tooltip></th>{compareMode && <th style={{ width: 90 }}><Tooltip text="How this attribute changed against the compared serial." placement="top"><span tabIndex={0} className="cursor-help">Change</span></Tooltip></th>}</tr></thead>
              <tbody>
                {inst.attributes.map((attr) => (
                  <tr key={attr.key}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text2)', verticalAlign: 'top' }}>{attr.key}</td>
                    <td style={{ verticalAlign: 'top' }}><AttributeValue attr={attr} /></td>
                    {compareMode && (
                      <td style={{ verticalAlign: 'top' }}>
                        {attr.change && attr.change !== 'unchanged'
                          ? <span style={{ fontSize: '0.62rem', color: CHANGE_STYLE[attr.change].color, fontFamily: 'var(--mono)' }}>{CHANGE_STYLE[attr.change].label}</span>
                          : null}
                      </td>
                    )}
                  </tr>
                ))}
                {inst.attributes.length === 0 && (
                  <tr><td colSpan={compareMode ? 3 : 2} style={{ color: 'var(--text3)', fontSize: '0.7rem' }}>No attributes.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}

